import type * as graphql from '@treams/treams-network';

// String values instead of the generated enums: jest has no alias for
// `@treams/treams-network`, so this module must not import it at runtime.
const OPEN = 'OPEN' as graphql.MomentStatus;
const COMPLETED = 'COMPLETED' as graphql.MomentStatus;
const TALENT_REVIEW = 'TALENT_REVIEW' as graphql.MomentType;

export type TalentReviewIndexMoment =
	graphql.TalentReviewIndexMomentsQuery['user']['moments'][number];

/**
 * The viewer's own talent review cycle moment per group for the index page:
 * the open moment with the earliest deadline (also when overdue), otherwise
 * the most recently submitted one. Mirrors the server's
 * `Group.talentReviewCycleMoment` selection, but resolved from one query for
 * all moments of the viewer instead of one partition read per group.
 * @param moments every talent review moment of the viewer, any status
 */
export function groupTalentReviewIndexMoments(
	moments: readonly TalentReviewIndexMoment[],
): Map<string, TalentReviewIndexMoment> {
	const byGroup = new Map<string, TalentReviewIndexMoment[]>();

	for (const moment of moments) {
		const groupID = moment.momentTemplate.talentReview?.group?.id;
		if (!groupID || moment.momentTemplate.type !== TALENT_REVIEW) {
			continue;
		}
		byGroup.set(groupID, [...(byGroup.get(groupID) ?? []), moment]);
	}

	const deadline = (moment: TalentReviewIndexMoment) =>
		moment.endDate ? new Date(moment.endDate).getTime() : Number.MAX_SAFE_INTEGER;

	const result = new Map<string, TalentReviewIndexMoment>();

	for (const [groupID, candidates] of byGroup) {
		const open = candidates
			.filter((moment) => moment.status === OPEN)
			.sort((a, b) => deadline(a) - deadline(b))[0];
		const submitted = candidates
			.filter((moment) => moment.status === COMPLETED)
			.sort((a, b) => deadline(b) - deadline(a))[0];
		const pick = open ?? submitted;
		if (pick) {
			result.set(groupID, pick);
		}
	}

	return result;
}
